if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      /* ── Initialize Swiper after DOM is ready ── */
      connectedCallback() {
        this.initMobileSwiper();
      }

      initMobileSwiper() {
        // Wait for Swiper library to be available
        if (typeof Swiper === 'undefined') {
          // Retry after a short delay if Swiper isn't loaded yet
          setTimeout(() => this.initMobileSwiper(), 100);
          return;
        }

        const swiperContainer = this.elements.viewer.querySelector('.swiper-container');
        if (!swiperContainer) return;

        const slides = swiperContainer.querySelectorAll('.swiper-slide');
        if (slides.length <= 1) return;

        // Ensure the <ul> has swiper-wrapper class
        const ulElement = swiperContainer.querySelector('ul');
        if (ulElement && !ulElement.classList.contains('swiper-wrapper')) {
          ulElement.classList.add('swiper-wrapper');
        }

        // Destroy existing swiper if any (prevents double-init)
        if (this.swiper) {
          this.swiper.destroy();
        }

        this.swiper = new Swiper(swiperContainer, {
          slidesPerView: 1,
          spaceBetween: 0,
          threshold: 5,
          touchRatio: 2,
          resistance: true,
          resistanceRatio: 0.85,
          on: {
            slideChange: () => {
              const activeSlide = this.swiper.slides[this.swiper.activeIndex];
              if (!activeSlide) return;
              const mediaId = activeSlide.dataset.mediaId;
              if (mediaId && this.elements.thumbnails) {
                const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
                this.setActiveThumbnail(thumbnail);
                this.announceLiveRegion(activeSlide, thumbnail?.dataset.mediaPosition);
                this.playActiveMedia(activeSlide);
              }
            }
          }
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        /* ── Swiper branch: let Swiper handle the sliding ── */
        if (this.swiper) {
          const index = Array.from(this.swiper.slides).indexOf(activeMedia);
          if (index !== -1) {
            this.swiper.slideTo(index);
          }
          this.preventStickyHeader();
          if (prepend && this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        } else {
          /* ── Fallback: original non-Swiper branch ── */
          const ulElement = this.elements.viewer.querySelector('ul');
          if (ulElement) {
            const swiperContainer = ulElement.querySelector('.swiper-container');
            if (swiperContainer) {
              const swiperWrapper = swiperContainer.querySelector('.swiper-wrapper');
              if (swiperWrapper) {
                swiperWrapper.style.setProperty('transform', 'translate3d(0px, 0px, 0px)');
              }
            }
          }

          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
            element.classList.remove('swiper-slide-active');
            element.classList.remove('is-active');
          });
          activeMedia.style.opacity = "0";
          activeMedia.style.transform = "scale(1.04)";

          requestAnimationFrame(() => {
            activeMedia.classList.add("swiper-slide-active");
            activeMedia.classList.add("is-active");

            requestAnimationFrame(() => {
              activeMedia.style.opacity = "1";
              activeMedia.style.transform = "scale(1)";
            });
          });
          
          if (prepend) {
            activeMedia.parentElement.prepend(activeMedia);
            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
            if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          }

          this.preventStickyHeader();
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            const activeMediaRect = activeMedia.getBoundingClientRect();
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
          this.playActiveMedia(activeMedia);
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (!this.swiper) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}